import { Copy, File, Folder, Loader2, X } from "lucide-react";
import { useCallback, useState } from "react";
import { FileAttachment } from "../../models/Message";
import { Util } from "../../services/Utils/Util";
import ImageViewer from "../components/ImageViewer";
import { UIRoomItem } from "../../models/RoomItem";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";

const MessageFileItem: React.FC<{ file: FileAttachment, files: FileAttachment[], showTitle: boolean }> = ({ file, files, showTitle }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const fileUrls = files.map(f => f.url);

  const isVideoFile = (filename: string) => {
    const ext = filename?.split('.').pop()?.toLowerCase() ?? "";
    return ['mp4', 'webm', 'mov', 'avi', 'mkv', 'm4v'].includes(ext);
  };

  const openImageViewer = useCallback((index: any) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  if (file.group == 'image') {
    return (
      <div className="relative inline-block overflow-hidden rounded-lg border border-gray-200 bg-white" onClick={(e) => e.stopPropagation()}>
        {file.isCopy && <Copy className="absolute bottom-1 right-1 h-3 w-3 text-gray-500 z-10" />}
        {!file.url && (
          <div className="flex h-20 w-20 items-center justify-center bg-gray-50">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        )}
        {file.url && (
          <img
            src={file.url}
            onClick={(e) => {
              e.stopPropagation();
              openImageViewer(fileUrls.findIndex(url => url == file.url));
            }}
            alt={file.filename}
            className="h-auto max-w-[200px] cursor-pointer object-cover hover:opacity-90"
            title={file.filename}
          />
        )}
        {showTitle && <p className="px-2 py-1 text-xs text-gray-700 truncate max-w-[200px]">{file.filename}</p>}
        {isViewerOpen && <ImageViewerModal images={fileUrls} currentImage={currentImage} onClose={closeImageViewer} />}
      </div>
    );
  } else if (isVideoFile(file.filename)) {
    // 동영상 파일은 앱 내에서 재생
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <div
          className="flex items-center gap-3 p-2 border border-gray-200 rounded-lg bg-white max-w-full overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsVideoOpen(true)}
        >
          <FileIcon filename={file.filename} />
          {!file.url && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          <div className="flex-1 min-w-0 overflow-hidden">
            <p className="text-sm font-medium text-gray-900 truncate break-all">{file.filename}</p>
            {file.sizeByte > 0 && <p className="text-xs text-gray-500">{Util.niceBytes(file.sizeByte)}</p>}
          </div>
          {file.isCopy && <Copy className="h-4 w-4 text-gray-400 shrink-0" />}
        </div>
        {isVideoOpen && <VideoViewerModal videoUrl={file.url} onClose={() => setIsVideoOpen(false)} />}
      </div>
    );
  } else {
    return (
      <a href={file.url || undefined} target="_blank" onClick={(e) => e.stopPropagation()} className="block text-inherit no-underline hover:bg-gray-50 rounded-lg transition-colors max-w-full">
        <div className="flex items-center gap-3 p-2 border border-gray-200 rounded-lg bg-white max-w-full overflow-hidden">
          <FileIcon filename={file.filename} />
          {!file.url && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          <div className="flex-1 min-w-0 overflow-hidden">
            <p className="text-sm font-medium text-gray-900 truncate break-all">{file.filename}</p>
            {file.sizeByte > 0 && <p className="text-xs text-gray-500">{Util.niceBytes(file.sizeByte)}</p>}
          </div>
          {file.isCopy && <Copy className="h-4 w-4 text-gray-400 shrink-0" />}
        </div>
      </a>
    )
  }
}

// FileIcon

const getFileColorStyles = (extension: string) => {
  const ext = extension.toLowerCase();

  const colors: Record<string, { bg: string, text: string, icon: string }> = {
    // Documents
    pdf: { bg: "bg-red-100", text: "text-red-700", icon: "text-red-500" },
    doc: { bg: "bg-blue-100", text: "text-blue-700", icon: "text-blue-500" },
    docx: { bg: "bg-blue-100", text: "text-blue-700", icon: "text-blue-500" },
    txt: { bg: "bg-gray-100", text: "text-gray-700", icon: "text-gray-500" },

    // Spreadsheets
    xls: { bg: "bg-green-100", text: "text-green-700", icon: "text-green-500" },
    xlsx: { bg: "bg-green-100", text: "text-green-700", icon: "text-green-500" },
    csv: { bg: "bg-green-100", text: "text-green-700", icon: "text-green-500" },

    // Presentations
    ppt: { bg: "bg-orange-100", text: "text-orange-700", icon: "text-orange-500" },
    pptx: { bg: "bg-orange-100", text: "text-orange-700", icon: "text-orange-500" },

    // Archives
    zip: { bg: "bg-yellow-100", text: "text-yellow-700", icon: "text-yellow-600" },
    rar: { bg: "bg-yellow-100", text: "text-yellow-700", icon: "text-yellow-600" },
    "7z": { bg: "bg-yellow-100", text: "text-yellow-700", icon: "text-yellow-600" },
    tar: { bg: "bg-yellow-100", text: "text-yellow-700", icon: "text-yellow-600" },
    gz: { bg: "bg-yellow-100", text: "text-yellow-700", icon: "text-yellow-600" },

    // Code
    js: { bg: "bg-yellow-50", text: "text-yellow-600", icon: "text-yellow-500" },
    ts: { bg: "bg-blue-50", text: "text-blue-600", icon: "text-blue-500" },
    jsx: { bg: "bg-blue-50", text: "text-blue-600", icon: "text-blue-500" },
    tsx: { bg: "bg-blue-50", text: "text-blue-600", icon: "text-blue-500" },
    html: { bg: "bg-orange-50", text: "text-orange-600", icon: "text-orange-500" },
    css: { bg: "bg-blue-50", text: "text-blue-600", icon: "text-blue-500" },
    json: { bg: "bg-gray-100", text: "text-gray-600", icon: "text-gray-500" },

    // Media
    mp3: { bg: "bg-purple-100", text: "text-purple-700", icon: "text-purple-500" },
    wav: { bg: "bg-purple-100", text: "text-purple-700", icon: "text-purple-500" },
    mp4: { bg: "bg-pink-100", text: "text-pink-700", icon: "text-pink-500" },
    avi: { bg: "bg-pink-100", text: "text-pink-700", icon: "text-pink-500" },
    mov: { bg: "bg-pink-100", text: "text-pink-700", icon: "text-pink-500" },

    // Images (fallback if not rendered as preview)
    jpg: { bg: "bg-indigo-100", text: "text-indigo-700", icon: "text-indigo-500" },
    jpeg: { bg: "bg-indigo-100", text: "text-indigo-700", icon: "text-indigo-500" },
    png: { bg: "bg-indigo-100", text: "text-indigo-700", icon: "text-indigo-500" },
    gif: { bg: "bg-indigo-100", text: "text-indigo-700", icon: "text-indigo-500" },
    svg: { bg: "bg-indigo-100", text: "text-indigo-700", icon: "text-indigo-500" },
  };

  return colors[ext] || { bg: "bg-gray-100", text: "text-gray-700", icon: "text-gray-500" };
};

export const FileIcon: React.FC<{ filename: string, className?: string }> = ({ filename, className }) => {
  const extension = filename?.split('.').pop()?.toLowerCase() ?? "";
  const styles = getFileColorStyles(extension);
  const displayExt = extension.length > 4 ? extension.slice(0, 3) : extension;

  return (
    <div className={cn("relative flex flex-col items-center justify-center rounded-lg overflow-hidden shrink-0", styles.bg, className || "h-10 w-10")}>
      <File className={cn("h-5 w-5 mb-2", styles.icon)} strokeWidth={2} />
      <span className={cn("absolute bottom-1 text-[9px] font-bold uppercase leading-none tracking-tighter", styles.text)}>
        {displayExt}
      </span>
    </div>
  );
};

export const FileIconList: React.FC<{ uitem: UIRoomItem }> = ({ uitem }) => {
  if (uitem.item.type === 'folder') {
    if (uitem.itemType === 'MessageFolder') {
      return (
        <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-blue-50 text-blue-500">
          <Folder className="h-6 w-6 fill-current" />
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-yellow-50 text-yellow-500">
        <Folder className="h-6 w-6 fill-current" />
      </div>
    );
  } else {
    const filename = uitem.item.file ? uitem.item.file.filename : uitem.item.url?.split('?')[0] ?? '';

    if (Util.isImageUrl(uitem.item.url)) {
      return <img src={uitem.item.url} className="h-10 w-10 object-cover rounded-lg border border-gray-100" alt="file" />
    }

    return <FileIcon filename={filename} className="h-10 w-10" />;
  }
}

export const FileIconThumb: React.FC<{ filename: string, url: string }> = ({ filename, url }) => {
  return <>
    {Util.isImageUrl(url) ?
      <span><img src={url} className="h-10 w-10 object-cover rounded" alt="thumb" /></span>
      :
      <span><FileIcon filename={filename} /></span>
    }
  </>
};


export const ImageViewerModal: React.FC<{ images: string[], currentImage: number, onClose: () => void }> = ({ images, currentImage, onClose }) => {
  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-none w-screen h-screen p-0 bg-black/90 border-none !rounded-none">
        <div className="relative w-full h-full flex items-center justify-center">
          <ImageViewer
            src={images}
            currentIndex={currentImage}
            onClose={onClose}
            backgroundStyle={{
              backgroundColor: "transparent"
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const VideoViewerModal: React.FC<{ videoUrl: string, onClose: () => void }> = ({ videoUrl, onClose }) => {
  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-none w-screen h-screen p-0 bg-black/95 border-none !rounded-none">
        <div className="relative w-full h-full flex items-center justify-center">
          {/* 닫기 버튼 */}
          <div className="absolute top-5 right-5 z-[51] flex gap-2">
            <a href={videoUrl} target="_blank" rel="noreferrer">
              <Button variant="ghost" size="icon" className="bg-black/30 hover:bg-black/50 text-white rounded-full h-10 w-10">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
              </Button>
            </a>
            <Button
              variant="ghost"
              size="icon"
              className="bg-black/30 hover:bg-black/50 text-white rounded-full h-10 w-10"
              onClick={onClose}
            >
              <X className="w-6 h-6" />
            </Button>
          </div>

          {/* 비디오 플레이어 */}
          <video
            controls
            autoPlay
            className="max-h-[90vh] max-w-[90vw] rounded-lg"
            src={videoUrl}
          >
            Your browser does not support the video tag.
          </video>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MessageFileItem;